import React, { useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { observer, inject } from 'mobx-react'
import CurrencyInput from '../../common/CurrencyInput'
import { useSortable } from '@dnd-kit/react/sortable'
import Input from '../../common/Input'
import { FieldTypeOptions, FieldTypeSelect } from './FieldTypeSelect'
import EditableInput from '../../common/EditableInput'
import ConfirmDialog from '../../common/ConfirmDialog'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete02Icon } from '@hugeicons/core-free-icons'

function Sortable ({ id, selectionStore, dataStore, index, item, typeSelectable }) {
  const [editing, setEditing] = useState(false)
  const [element, setElement] = useState(null)
  const [isHovered, setIsHovered] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const handleRef = useRef(null)
  const { isDragging } = useSortable({ id, index, element, handle: handleRef })

  if (!selectionStore.item) return null
  if (typeof selectionStore.item.updateAdditionalInfoItem !== 'function') return null

  const field = FieldTypeOptions.find(option => option.type === item.type)
  const input = (typeof selectionStore.item.getAdditionalInfoItemValue === 'function'
    ? selectionStore.item.getAdditionalInfoItemValue(item.id)
    : null) || item.input

  const handleInputChange = selectionStore.item.updateAdditionalInfoItem
  const handleTypeChange = selectionStore.item.updateAdditionalInfoItemType
  const handleBlur = () => selectionStore.item.debouncedSaveAdditionalInfo.flush()

  const confirmDelete = async () => {
    setDeleting(true)
    await dataStore.deleteUserAdditionalInfoItem(item.id)
    // On success the item leaves the list and this row unmounts; only reset if it's still here.
    setDeleting(false)
    setConfirmingDelete(false)
  }

  return (
    <>
      <div ref={setElement} className='sortable_item' data-shadow={isDragging || undefined}>
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: '30px',
            width: '100%',
            ...(isHovered ? { borderRadius: '5px', backgroundColor: '#F0FAFB' } : {})
          }}
        >
          {isHovered ? <button ref={handleRef} className='handle' /> : null}
          <div>
            <EditableInput
              editing={editing}
              setEditing={setEditing}
              value={(item.input && item.input.title) || ''}
              placeholder='Title'
              onSave={value => dataStore.updateUserAdditionalInfoItem(item.id, { input: { title: value } })}
            />
          </div>
          <div style={{ fontSize: '10px', color: '#6E798A' }}>
            {typeSelectable
              ? (
                <FieldTypeSelect
                  field={field}
                  setField={selectedOption => {
                    handleTypeChange(item.id, selectedOption.type, selectedOption.label)
                    if (handleBlur) handleBlur()
                  }}
                />
                )
              : null}
          </div>
          {isHovered && (
          <div>
            <button onClick={() => setConfirmingDelete(true)}>
              <HugeiconsIcon icon={Delete02Icon} size={12} />
            </button>
          </div>
          )}
        </div>
        <div>
          {
          item.type === 'notes' && (
          <textarea
            className='input glass'
            placeHolder='Enter notes here ...'
            style={{ width: '100%', paddingTop: '4px', height: '80px', borderColor: 'hsla(218, 11%, 81%, 1)' }}
            value={selectionStore.item.notes || ''}
            onChange={event => selectionStore.item.updateNotes(event.target.value)}
            onBlur={handleBlur}
          />
          )
        }
          {
          item.type === 'date' && (
            <input
              className='input glass input_rounded'
              style={{ width: '100%' }}
              type='date'
              data-empty={(input && input.value) ? undefined : true}
              value={(input && input.value != null) ? input.value : ''}
              onChange={event => handleInputChange(item.id, 'value', event.target.value)}
              onBlur={handleBlur}
            />
          )
        }
          {
          item.type === 'currency' && (
            <CurrencyInput
              currency={(input && input.currency != null) ? input.currency : 'USD'}
              value={(input && input.value != null) ? input.value : ''}
              onCurrencyChange={currency => dataStore.updateUserAdditionalInfoItem(item.id, { input: { currency: currency.value } })}
              onValueChange={value => handleInputChange(item.id, 'value', value)}
              onValueBlur={handleBlur}
            />
          )
        }
          {
          item.type === 'number' && (
            <Input
              className='glass input_rounded'
              type='number'
              placeholder={12345}
              input={(input && input.value != null) ? input.value : ''}
              value={input.value}
              onChange={event => handleInputChange(item.id, 'value', event.target.value)}
              onBlur={handleBlur}
            />
          )
        }
          {
          item.type === 'text' && (
            <Input
              className='input_rounded'
              placeholder='Enter text here'
              type='text'
              value={(input && input.value != null) ? input.value : ''}
              onChange={event => handleInputChange(item.id, 'value', event.target.value)}
              onBlur={handleBlur}
            />
          )
        }
        </div>
      </div>
      {confirmingDelete && (
        <ConfirmDialog
          title='Delete field?'
          confirmLabel='Delete'
          confirmKind='danger'
          busy={deleting}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={confirmDelete}
        >
          <p>This field will be deleted for all the shapes, markers, folders and boundaries.</p>
          <p>Are you sure you want to proceed?</p>
        </ConfirmDialog>
      )}
    </>
  )
}

export default withTranslation()(
  inject('selectionStore', 'dataStore')(observer(Sortable))
)
